import { useEffect, useMemo, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import { useDispatch } from 'react-redux'

import Attachment from './Attachment'
import downloadFile from '../utils/DownloadFile'
import { raiseToast } from '../services/redux/reducers/toast/toast'
import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import 'react-pdf/dist/esm/Page/TextLayer.css'
import './index.css'

pdfjs.GlobalWorkerOptions.workerSrc = `/pdf.worker.min.mjs`

const zoomOptions = [
    { label: 'Actual size', value: 1 },
    { label: 'Page fit', value: 'fit' },
    { label: 'Page width', value: 'width' },
    { label: '50%', value: 0.5 },
    { label: '75%', value: 0.75 },
    { label: '100%', value: 1 },
    { label: '125%', value: 1.2 },
    { label: '150%', value: 1.5 },
    { label: '200%', value: 2 },
    { label: '300%', value: 3 },
    { label: '400%', value: 4 }
]

const isPDF = (url) => {
    if (typeof url !== 'string'){
        return false
    }

    return url.toLowerCase().endsWith('.pdf')
}

const isImage = (url) => {
    if (typeof url !== 'string'){
        return false
    }

    const imageExtensions = ['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp', 'svg']
    const ext = url.split('.').pop().toLowerCase()
    return imageExtensions.includes(ext)
}

const DocumentView = (props) => {
    const { fileUrl, showZoomControl } = props
    
    const dispatch = useDispatch()

    const [zoom, setZoom] = useState(isPDF(fileUrl) ? 1.3 : 'width')
    const [rotation, setRotation] = useState(0)
    const [dropdownOpen, setDropdownOpen] = useState(false)
    const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 })
    const [numPages, setNumPages] = useState(null)

    const zoomLabel = zoomOptions.find(o => o.value === zoom)?.label || `${zoom * 100}%`
    const numericOptions = zoomOptions.filter(o => typeof o.value === 'number')

    const options = useMemo(() => ({
        cMapUrl: 'https://unpkg.com/pdfjs-dist@3.11.174/cmaps/',
        cMapPacked: true
    }), [])

    const onImageLoad = (e) => {
        setNaturalSize({ w: e.target.naturalWidth, h: e.target.naturalHeight })
    }

    const handleZoomIn = () => {
        let current = typeof zoom === 'number' ? zoom : 1
        const idx = numericOptions.findIndex(eachOption => eachOption.value > current)
        if (idx !== -1){
            setZoom(numericOptions[idx].value)
        }
    }

    const handleZoomOut = () => {
        let current = typeof zoom === 'number' ? zoom : 1
        const reversed = [...numericOptions].reverse()
        const idx = reversed.findIndex(eachOption => eachOption.value < current)
        if (idx !== -1){
            setZoom(reversed[idx].value)
        }
    }

    const renderZoomControls = () => (
        <div className='zoom-controls'>
            <button onClick={handleZoomOut} className='pi pi-search-minus icon-btn' title='Zoom Out' />
            <div className='custom-dropdown' onClick={e => { e.stopPropagation(); setDropdownOpen(!dropdownOpen) }}>
                {zoomLabel} <span className='arrow'>▾</span>
                {dropdownOpen && (
                    <div className='dropdown-options'>
                        {zoomOptions.map(opt => (
                            <div
                                key={opt.value}
                                className='dropdown-option-item'
                                onClick={e => { e.stopPropagation(); setZoom(opt.value); setDropdownOpen(false) }}
                            >
                                {opt.label}
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <button onClick={handleZoomIn} className='pi pi-search-plus icon-btn' title='Zoom In' />
            {isImage(fileUrl) && (
                <>
                    <button onClick={() => setRotation(r => r + 90)} className='pi pi-refresh icon-btn' title='Rotate Right' />
                    <button onClick={() => setRotation(r => r - 90)} className='pi pi-replay icon-btn' title='Rotate Left' />
                </>
            )}
            <button onClick={() => handleDownload(fileUrl)} className='pi pi-download icon-btn' title='Download' />
        </div>
    )

    const handleDownload = (url) => {
        if (url){
            downloadFile(url, 'document')
        }
        else{
            dispatch(raiseToast({ severity: 'error', detail: 'File was not found' }))
        }
    }

    const onDocumentLoadSuccess = ({ numPages }) => setNumPages(numPages)

    const renderImage = () => {
        const widthStyle = typeof zoom === 'number' ? naturalSize.w * zoom : zoom === 'width' ? '100%' : 'auto'
        const heightStyle = zoom === 'fit' ? '100%' : 'auto'

        return (
            <div style={{ overflow: 'auto',maxHeight: 'calc(100% - 40px)', textAlign: 'center' }}>
                <img
                    src={fileUrl}
                    onLoad={onImageLoad}
                    alt='expanded attachment'
                    style={{
                        display: 'inline-block',
                        width: typeof widthStyle === 'number' ? `${widthStyle}px` : widthStyle,
                        height: heightStyle,
                        transform: `rotate(${rotation}deg)`,
                        transformOrigin: 'center center',
                        transition: 'all 0.3s ease'
                    }}
                />
            </div>
        )
    }

    const renderPDF = () => {
        return (
            <div className='flex flex-column h-full w-full' style={{ width: '85vw', overflow: 'auto',maxHeight: 'calc(100% - 40px)', textAlign: 'center', paddingBottom: '12px'  }}>
                <Document
                    file={fileUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={(error) => console.error('PDF load error:', error)}
                    options={options}
                >
                    {Array.from(new Array(numPages), (_, idx) => (
                        <Page
                            key={`page_${idx + 1}`}
                            pageNumber={idx + 1}
                            scale={typeof zoom === 'number' ? zoom : 1.3}
                            renderAnnotationLayer={false}
                            renderTextLayer={true}
                        />
                    ))}
                </Document>
            </div>
        )
    }

    const renderFile = () => {
        if (typeof fileUrl !== 'string'){
            return null
        }
        if (isPDF(fileUrl)){
            return renderPDF()
        }
        if (isImage(fileUrl)){
            return renderImage()
        }
        return <Attachment url={fileUrl} displayFileName={true} />
    }

    return (
        <>
            {showZoomControl && renderZoomControls()}
            {renderFile()}
        </>
    )
}

export default DocumentView
